import { cn } from "@/utils/cn";
import { IonSearchbar } from "@ionic/react";
import { memo, useRef } from "react";

interface SearchInputProps {
  id?: string;
  placeholder?: string;
  className?: string;
  onFocus?: () => void;
  onBlur?: (value: string) => void;
  onEnter?: (value: string) => void;
  onClear?: () => void;
  onClick?: () => void;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  blurOnEnter?: boolean;
}

export const SearchInput = memo(function SearchInput({
  id,
  placeholder,
  className,
  onFocus,
  onBlur,
  onClear,
  onEnter,
  onClick,
  defaultValue = "",
  onValueChange,
  blurOnEnter = false,
}: SearchInputProps) {
  const valueRef = useRef<string>(defaultValue);
  const searchbarRef = useRef<HTMLIonSearchbarElement | null>(null);

  const blurSearchInput = () => {
    if (!blurOnEnter) {
      return;
    }
    searchbarRef.current
      ?.getInputElement()
      .then((element) => element?.blur?.())
      .catch(() => {
        console.warn("Failed to blur search input");
      });
  };

  return (
    <IonSearchbar
      id={id}
      ref={(element) => {
        searchbarRef.current = element;
      }}
      color="transparent"
      mode="ios"
      showClearButton="focus"
      className={cn(
        "input-outline-none bg-gray-100 rounded-lg min-h-5! p-1!",
        className,
      )}
      placeholder={placeholder}
      onIonInput={(e) => {
        const value = e.detail.value ?? "";
        valueRef.current = value;
        onValueChange?.(value);
      }}
      onIonChange={(e) => {
        const value = e.detail.value ?? "";
        valueRef.current = value;
        onValueChange?.(value);
      }}
      onFocus={onFocus}
      onBlur={() => {
        const trimmedValue = (valueRef.current ?? "").trim();
        valueRef.current = trimmedValue;
        onBlur?.(trimmedValue);
      }}
      onIonClear={() => {
        valueRef.current = "";
        onValueChange?.("");
        onClear?.();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          const trimmed = (valueRef.current ?? "").trim();
          valueRef.current = trimmed;
          onValueChange?.(trimmed);
          blurSearchInput();
          onEnter?.(trimmed);
        }
      }}
      onClick={onClick}
    />
  );
});
